import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronRight, Eye, Loader2, Mail, UserPlus, Users } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import type { Workspace } from "@/types/workspace";
import axiosInstance from "@/utils/appsettings";
import { cn } from "@/lib/utils";

export const INVITE_ROLES = [
    { value: "editor", label: "Editor", detail: "Can build in every project. Sees the plan and credits but cannot change billing or invite people." },
    { value: "admin", label: "Admin", detail: "Can manage billing, members, and all projects." },
];

export const INVITE_UPGRADE_MESSAGE = "Upgrade to Solo Builder or above to invite people.";

// A lapsed paid plan also reads team_collaboration false, but it needs renewing, not upgrading; the server already says so (UAT 1029l8qb F6)
export const isInvitePlanLapsed = (workspace: Workspace | null | undefined) =>
    ["expired", "past_due"].includes(String(workspace?.subscription?.status));

// A personal workspace can take members only when its own plan includes team collaboration; team workspaces are never gated here.
// A team with no plan summary predates plans being linked to teams and keeps inviting; any other workspace needs a plan with members
export const canInviteToWorkspace = (workspace: Workspace | null | undefined) =>
    !!workspace && (workspace.type !== "personal" && !workspace.subscription ? true : workspace.subscription?.team_collaboration === true);

const INVALID_EMAIL_MESSAGE = "Enter a valid email address, like name@example.com.";

// Deliberately loose (one @, a dotted domain, no spaces): the server is the real check, this just stops obvious typos like "not-an-email".
const isValidInviteEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

// FastAPI sends a string detail for 400s and a list of {loc, msg} for 422 validation errors, so both are turned into one sentence.
const readableInviteError = (err: unknown): string => {
    const response = (err as { response?: { status?: number; data?: { detail?: unknown } } }).response;
    const detail = response?.data?.detail;
    // The only body field the server validates is the email, and its 422 text is raw Pydantic ("value is not a valid email address: ...") (UAT 1029l8qb F21)
    if (response?.status === 422) return INVALID_EMAIL_MESSAGE;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail)) {
        const emailIssue = detail.some((d) => Array.isArray(d?.loc) && d.loc.includes("email"));
        if (emailIssue) return INVALID_EMAIL_MESSAGE;
        const msgs = detail.map((d) => (typeof d?.msg === "string" ? d.msg : null)).filter(Boolean);
        if (msgs.length) return msgs.join(". ");
    }
    if (response?.status === 422) return INVALID_EMAIL_MESSAGE;
    return "Could not send invitation.";
};

interface InviteMembersDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    workspace: Workspace;
    onInvited?: () => void | Promise<void>;
    // Opens the separate-team checkout; the parent owns that dialog because it needs the Stripe provider
    onCreateSeparateWorkspace?: () => void;
}

function SeparateWorkspaceOption({ onClick }: { onClick: () => void }) {
    return (
        <div className="rounded-xl border border-line bg-soft px-4 py-3">
            <button
                type="button"
                onClick={onClick}
                className="inline-flex items-center gap-0.5 text-[13px] font-semibold text-plum underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-plum/40 rounded-sm"
            >
                Create a separate workspace instead
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
            <p className="mt-0.5 text-[12px] leading-relaxed text-muted-foreground">It has its own plan and its own projects.</p>
        </div>
    );
}

export function InviteMembersDialog({ open, onOpenChange, workspace, onInvited, onCreateSeparateWorkspace }: InviteMembersDialogProps) {
    const { refreshWorkspaces } = useWorkspace();
    const { toast } = useToast();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState<string | null>(null);
    const [role, setRole] = useState("editor");
    const [inviting, setInviting] = useState(false);
    // Captured when the dialog opens so the copy doesn't flip to "team" while the conversion response closes it
    const [wasPersonal, setWasPersonal] = useState(workspace.type === "personal");

    useEffect(() => {
        if (open) {
            setWasPersonal(workspace.type === "personal");
            // A fresh form each time: leaving for the separate-workspace checkout and back must not keep the old address
            setEmail("");
            setRole("editor");
            setEmailError(null);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const eligible = canInviteToWorkspace(workspace);
    const lapsed = isInvitePlanLapsed(workspace);
    const roleDetail = INVITE_ROLES.find(r => r.value === role)?.detail;

    const createSeparateWorkspace = onCreateSeparateWorkspace
        ? () => { onOpenChange(false); onCreateSeparateWorkspace(); }
        : undefined;

    const handleInvite = async () => {
        const trimmed = email.trim();
        if (!trimmed) return;
        if (!isValidInviteEmail(trimmed)) {
            setEmailError(INVALID_EMAIL_MESSAGE);
            return;
        }
        setEmailError(null);
        setInviting(true);
        try {
            const res = await axiosInstance.post(`/workspaces/${workspace.id}/members/invite`, { email: trimmed, role });
            // Same workspace id, now typed "team": re-read the list so the switcher and pages change in place
            if (res?.data?.converted_to_team) await refreshWorkspaces();
            const previousRole = res?.data?.previous_role;
            toast({
                title: res?.data?.resent ? "Invitation Re-sent" : "Invitation Sent",
                description: previousRole && previousRole !== role
                    ? `${trimmed} already had an invite as ${previousRole}. It was re-sent as ${role}.`
                    : res?.data?.resent ? `${trimmed} already had an invite. It was sent again.` : `Invited ${trimmed} as ${role}.`,
                className: "bg-success-bg border-success/25 text-success",
            });
            await onInvited?.();
            setEmail("");
            setRole("editor");
            onOpenChange(false);
        } catch (err) {
            const message = readableInviteError(err);
            // A 422 is the server's own email check, so the message belongs next to the field as well
            if ((err as { response?: { status?: number } }).response?.status === 422) setEmailError(message);
            toast({
                variant: "destructive",
                title: "Invite Failed",
                description: message,
            });
        } finally {
            setInviting(false);
        }
    };

    if (!eligible) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="sm:max-w-md grid-rows-none overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl text-foreground">Invite people to {workspace.name}</DialogTitle>
                        <DialogDescription>
                            {lapsed ? `Your ${workspace.subscription?.plan_name ? `${workspace.subscription.plan_name} ` : ""}plan has lapsed. Renew it to invite people.` : INVITE_UPGRADE_MESSAGE}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex gap-3 rounded-xl border border-plum/20 bg-selected px-4 py-3.5">
                        <UserPlus className="mt-0.5 h-4 w-4 shrink-0 text-plum" aria-hidden="true" />
                        <p className="text-[13px] leading-relaxed text-foreground">
                            {lapsed
                                ? "Renewing brings back inviting for this workspace. Your projects and members stay right where they are."
                                : "Solo Builder, Starter and Co-Build plans let you bring people into this workspace. Your projects, plan and credits stay right where they are."}
                        </p>
                    </div>
                    {createSeparateWorkspace && <SeparateWorkspaceOption onClick={createSeparateWorkspace} />}
                    <DialogFooter>
                        <Button variant="ghost" className="bg-surface outline outline-1 outline-line text-foreground" onClick={() => onOpenChange(false)}>Not now</Button>
                        <Button
                            className="bg-gradient-sunset text-white hover:opacity-95"
                            onClick={() => { onOpenChange(false); navigate("/billing/plans"); }}
                        >
                            {lapsed ? "Renew plan" : "See plans"} <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!inviting) onOpenChange(next); }}>
            {/* Several direct children: the default minmax(0,1fr) first row collapses the header into the form once the dialog hits max-h */}
            <DialogContent className="sm:max-w-md grid-rows-none overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-xl text-foreground">
                        {workspace.name ? `Invite to ${workspace.name}` : "Invite Team Member"}
                    </DialogTitle>
                    <DialogDescription>
                        Send an invitation email to add a new member to {workspace.name || "this workspace"}.
                    </DialogDescription>
                </DialogHeader>
                <form className="space-y-4 py-2" onSubmit={(e) => { e.preventDefault(); if (!inviting) handleInvite(); }}>
                    <div className="space-y-2">
                        <Label htmlFor="email" className="text-foreground font-medium">Email Address</Label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                id="email"
                                type="email"
                                placeholder="colleague@example.com"
                                className={cn(
                                    "pl-9 bg-surface border-line focus:border-plum/40 text-foreground placeholder:text-muted-foreground",
                                    emailError && "border-danger focus:border-danger",
                                )}
                                value={email}
                                aria-invalid={!!emailError}
                                aria-describedby={emailError ? "invite-email-error" : undefined}
                                onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(null); }}
                            />
                        </div>
                        {emailError && (
                            <p id="invite-email-error" role="alert" className="text-xs font-medium text-danger">{emailError}</p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="role" className="text-foreground font-medium">Role</Label>
                        <Select value={role} onValueChange={setRole}>
                            <SelectTrigger id="role" className="bg-surface border-line text-foreground">
                                <SelectValue placeholder="Select a role" />
                            </SelectTrigger>
                            <SelectContent>
                                {INVITE_ROLES.map(r => (
                                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">{roleDetail}</p>
                    </div>
                </form>
                {/* Sizes sit on each <p>: the global p rule in index.css (1.125rem) beats a size inherited from the wrapper */}
                <div data-testid="invite-visibility-notice" className="space-y-2.5 rounded-xl border border-plum/20 bg-selected px-4 py-3.5 text-foreground">
                    <p className="flex gap-2.5 text-[13px] leading-relaxed">
                        <Eye className="mt-0.5 h-4 w-4 shrink-0 text-plum" aria-hidden="true" />
                        <span>Members will see all existing projects in <span className="font-semibold">{workspace.name}</span>.</span>
                    </p>
                    {wasPersonal && (
                        <p className="flex gap-2.5 text-[13px] leading-relaxed">
                            <Users className="mt-0.5 h-4 w-4 shrink-0 text-plum" aria-hidden="true" />
                            <span>People you invite share this workspace's projects, plan and credits.</span>
                        </p>
                    )}
                </div>
                {createSeparateWorkspace && <SeparateWorkspaceOption onClick={createSeparateWorkspace} />}
                <DialogFooter>
                    <Button variant="ghost" disabled={inviting} className="bg-surface outline outline-1 outline-line text-foreground" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={handleInvite} disabled={inviting || !email.trim() || !!emailError} className="bg-gradient-sunset text-white hover:opacity-95">
                        {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                        Send Invitation
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
